import { useState, useEffect } from 'react';
import { writeSharedConsentCookies, clearSharedConsentCookies } from '../lib/consentCookies';

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export const useCookieConsent = () => {
  const [consent, setConsent] = useState<CookiePreferences | null>(null);
  const [hasConsented, setHasConsented] = useState(false);

  useEffect(() => {
    const storedConsent = localStorage.getItem('cookie-consent');
    const storedPreferences = localStorage.getItem('cookie-preferences');

    if (storedConsent && storedPreferences) {
      setHasConsented(true);
      const parsed = JSON.parse(storedPreferences) as CookiePreferences;
      setConsent(parsed);
      // Backfill shared cookies for users who consented before the cross-
      // subdomain change shipped, so the app subdomain honors their decision
      // without re-prompting.
      if (!document.cookie.includes('ns_consent_given=')) {
        writeSharedConsentCookies(parsed);
      }
    }

    const handleChange = (event: Event) => {
      const custom = event as CustomEvent<CookiePreferences | null>;
      if (!custom.detail) {
        setConsent(null);
        setHasConsented(false);
        return;
      }
      setConsent(custom.detail);
      setHasConsented(true);
    };

    window.addEventListener('cookie-consent-changed', handleChange);
    return () => {
      window.removeEventListener('cookie-consent-changed', handleChange);
    };
  }, []);

  const updateConsent = (preferences: CookiePreferences) => {
    const now = Date.now().toString();
    localStorage.setItem('cookie-consent', 'accepted');
    localStorage.setItem('cookie-consent-date', now);
    localStorage.setItem('cookie-preferences', JSON.stringify(preferences));
    writeSharedConsentCookies(preferences);
    setConsent(preferences);
    setHasConsented(true);
  };

  const revokeConsent = () => {
    localStorage.removeItem('cookie-consent');
    localStorage.removeItem('cookie-consent-date');
    localStorage.removeItem('cookie-preferences');
    clearSharedConsentCookies();
    setConsent(null);
    setHasConsented(false);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent<CookiePreferences | null>('cookie-consent-changed', {
          detail: null,
        })
      );
    }
  };

  return {
    consent,
    hasConsented,
    updateConsent,
    revokeConsent,
    hasAnalyticsConsent: consent?.analytics || false,
    hasMarketingConsent: consent?.marketing || false,
  };
};